import {
  QUESTION_TYPE_NODES,
  QUESTION_SINGLE,
  QUESTION_MULTIPLE,
  QUESTION_OPEN_ENDED,
  QUESTION_NUMERIC_OPEN_ENDED,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MATRIX_CONSTANT_SUM,
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_NUMERIC_OPEN_ENDED,
  QUESTION_MATRIX_BIPOLAR,
  QUESTION_CONVERSATIONAL,
  QUESTION_MATRIX_OPEN_ENDED_TYPES,
  QUESTION_MATRIX_TYPES,
  QUESTION_NEW_MATRIX_TYPE_NODES,
  QUESTION_RANKING,
  QUESTION_SLIDER,
  QUESTION_DRILLDOWN,
  QUESTION_VAN_WESTENDORP,
  QUESTION_NPS,
  QUESTION_RATING,
  QUESTION_DESCRIPTION,
  QUESTION_OPEN_ENDED_PEEQ,
  QUESTION_NUMERIC_OPEN_ENDED_PEEQ,
  GROUP_BASIC,
  GROUP_AB_TEST,
  GROUP_CONJOINT,
  GROUP_VAN_WESTENDORP,
  GROUP_MAXDIFF,
  GROUP_LOOP_MERGE,
  GROUP_TYPES,
  RESEARCH_METHOD_TYPES,
} from 'data/nodeTypes';
import { and, or, not } from 'utils/combinators';

const getTypeFromTypeOrNode = (value) =>
  typeof value === 'object' ? getNodeTypeOrStyle(value) : value;

const getNodeTypeOrStyle = (node) => node?.type ?? node?.style;
const getNodeStyleOrType = (node) => node?.style ?? node?.type;

const fromNodeOrType = (predicate) => (value) =>
  predicate(getTypeFromTypeOrNode(value));

const fromNode =
  (predicate, typeResolver = getNodeTypeOrStyle) =>
  (node) =>
    predicate(typeResolver(node));

// ------------- Question node types utilities with argument type ---------------

export const isQuestionType = (type) => QUESTION_TYPE_NODES.includes(type);

export const isSingleQuestionType = (type) => type === QUESTION_SINGLE;

export const isMultipleQuestionType = (type) => type === QUESTION_MULTIPLE;

export const isOpenEndedQuestionType = (type) => type === QUESTION_OPEN_ENDED;

export const isNumericOpenEndedQuestionType = (type) =>
  type === QUESTION_NUMERIC_OPEN_ENDED;

export const isBipolarQuestionType = (type) => type === QUESTION_MATRIX_BIPOLAR;

export const isMatrixNumericQuestionType = (type) =>
  type === QUESTION_MATRIX_NUMERIC_OPEN_ENDED;

export const isMatrixConstantSumType = (type) =>
  type === QUESTION_MATRIX_CONSTANT_SUM;

export const isConversationalType = (type) => type === QUESTION_CONVERSATIONAL;

export const isMatrixSingleQuestionType = (type) =>
  type === QUESTION_MATRIX_SINGLE;

export const isMatrixMultipleQuestionType = (type) =>
  type === QUESTION_MATRIX_MULTIPLE;

export const isMatrixOpenEndedQuestionType = (type) =>
  type === QUESTION_MATRIX_OPEN_ENDED;

export const isRankingQuestionType = (type) => type === QUESTION_RANKING;

export const isSliderQuestionType = (type) => type === QUESTION_SLIDER;

export const isDrillDownQuestionType = (type) => type === QUESTION_DRILLDOWN;

export const isVanWestendorpQuestionType = (type) =>
  type === QUESTION_VAN_WESTENDORP;

export const isNpsQuestionType = (type) => type === QUESTION_NPS;

export const isRatingQuestionType = (type) => type === QUESTION_RATING;

export const isDescriptionQuestionType = (type) =>
  type === QUESTION_DESCRIPTION;

export const isOpenEndedPeeqQuestionType = (type) =>
  type === QUESTION_OPEN_ENDED_PEEQ;

export const isNumericOpenEndedPeeqQuestionType = (type) =>
  type === QUESTION_NUMERIC_OPEN_ENDED_PEEQ;

export const isMatrixQuestionType = (type) =>
  QUESTION_MATRIX_TYPES.includes(type);

export const isMatrixOpenEndedType = (type) =>
  QUESTION_MATRIX_OPEN_ENDED_TYPES.includes(type);

export const isMatrixSingleMultipleQuestionType = (type) =>
  or(isMatrixSingleQuestionType, isMatrixMultipleQuestionType)(type);

export const isSingleOrMultipleQuestionType = (type) =>
  or(isSingleQuestionType, isMultipleQuestionType)(type);

export const isMultipleOrMatrixMultipleQuestionType = (type) =>
  or(isMultipleQuestionType, isMatrixMultipleQuestionType)(type);

export const isMatrixAndNotBipolarQuestionType = (type) =>
  and(isMatrixQuestionType, not(isBipolarQuestionType))(type);

export const isMatrixSingleMultipleOrRankingQuestionType = (type) =>
  or(isMatrixSingleMultipleQuestionType, isRankingQuestionType)(type);

export const isMatrixSingleOrBipolarQuestionType = (type) =>
  or(isMatrixSingleQuestionType, isBipolarQuestionType)(type);

export const isMatrixSingleMultipleOrBipolarQuestionType = (type) =>
  or(isMatrixSingleMultipleQuestionType, isBipolarQuestionType)(type);

export const isMatrixNumericOrConstantSumQuestionType = (type) =>
  or(isMatrixNumericQuestionType, isMatrixConstantSumType)(type);

export const isMatrixBipolarOrConstantSumQuestionType = (type) =>
  or(isBipolarQuestionType, isMatrixConstantSumType)(type);

export const isOpenEndedOrNumericOpenEndedQuestionType = (type) =>
  or(isOpenEndedQuestionType, isNumericOpenEndedQuestionType)(type);

export const isOpenEndedOrNumericOpenEndedOrSliderQuestionType = (type) =>
  or(isOpenEndedOrNumericOpenEndedQuestionType, isSliderQuestionType)(type);

// ------------- Question node types utilities with argument node ---------------
export const isQuestionNode = fromNode(isQuestionType);

export const isMultipleNode = fromNode(isMultipleQuestionType);

export const isSingleOrMultipleNode = fromNode(isSingleOrMultipleQuestionType);

export const isSliderNode = fromNode(isSliderQuestionType);

export const isDrillDownNode = fromNode(isDrillDownQuestionType);

export const isConversationalNode = fromNode(isConversationalType);

export const isVanWestendorpQuestionNode = fromNode(
  isVanWestendorpQuestionType,
);

export const isNotVanWestendorpQuestionNode = (node) =>
  not(isVanWestendorpQuestionNode)(node);

export const isNpsNode = fromNode(isNpsQuestionType);

export const isDescriptionNode = fromNode(isDescriptionQuestionType);

export const isMatrixNode = fromNode(isMatrixQuestionType, getNodeStyleOrType);

export const isNewMatrixQuestionType = (type) =>
  QUESTION_NEW_MATRIX_TYPE_NODES.includes(type);

export const isNewMatrixNode = fromNode(isNewMatrixQuestionType);

export const isMatrixSingleOrMultipleNode = fromNode(
  isMatrixSingleMultipleQuestionType,
);

export const isConstantSumNode = fromNode(isMatrixConstantSumType);

export const isBipolarNode = fromNode(isBipolarQuestionType);

export const isMatrixBipolarOrConstantNode = fromNode(
  isMatrixBipolarOrConstantSumQuestionType,
);

// ------------- Group node types utilities ---------------

export const isGroup = fromNodeOrType((type) => GROUP_TYPES.includes(type));

export const isResearchMethod = fromNodeOrType((type) =>
  RESEARCH_METHOD_TYPES.includes(type),
);

export const isAbGroup = (type) => type === GROUP_AB_TEST;
export const isBasicGroup = (type) => type === GROUP_BASIC;
export const isConjointGroup = (type) => type === GROUP_CONJOINT;
export const isVanWestendorpGroup = (type) => type === GROUP_VAN_WESTENDORP;
export const isLoopAndMergeGroup = (type) => type === GROUP_LOOP_MERGE;
export const isMaxDiffGroup = (type) => type === GROUP_MAXDIFF;
export const isConjointOrMaxDiffGroup = (type) =>
  isConjointGroup(type) || isMaxDiffGroup(type);
export const isBasicOrLoopAndMergeOrConjointOrMaxDiffGroup = (type) =>
  or(isBasicGroup, isLoopAndMergeGroup, isConjointOrMaxDiffGroup)(type);
export const isAbOrVanWestendorpGroup = (type) =>
  or(isAbGroup, isVanWestendorpGroup)(type);
export const isAbOrVanWestendorpOrConjointOrMaxDiffGroup = (type) =>
  or(isAbOrVanWestendorpGroup, isConjointOrMaxDiffGroup)(type);

export const isBasicGroupNode = fromNode(isBasicGroup);
export const isAbGroupNode = fromNode(isAbGroup);
export const isConjointGroupNode = fromNode(isConjointGroup);
export const isMaxDiffGroupNode = fromNode(isMaxDiffGroup);
export const isConjointOrMaxDiffGroupNode = fromNode(isConjointOrMaxDiffGroup);
export const isLoopAndMergeGroupNode = fromNode(isLoopAndMergeGroup);
export const isVanWestendorpGroupNode = fromNode(isVanWestendorpGroup);
